import { useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import type { ConflictWarning, ResolvedBracketGame } from "@alpha/engine";
import { api, apiErrorMessage } from "../../../lib/api";
import { trackGameCreated } from "../../../lib/analytics";
import { useAdminMutation } from "../../../lib/adminData";
import { bracketGamesById, poolTeamOptions, slotLabel, teamLabelById, teamName, type TeamOption } from "../../../lib/bundle";
import { cx } from "../../../lib/cx";
import { dayLabel, gameDayKey, groupByDay } from "../../../lib/gameDay";
import { latestGame, nextGameSlot } from "../../../lib/gameDefaults";
import { runSequentially } from "../../../lib/sequence";
import type { Caps } from "../../../lib/tournamentPhases";
import type { Bundle, Game, Pool } from "../../../lib/types";
import { GameScheduleLine } from "../../../components/GameScheduleLine";
import { Badge, Button, ButtonLink, GameStatusBadge, Input, SectionCard, Select } from "../../../components/ui";
import { phasePath } from "./PhaseRail";
import type { TournamentContext } from "./TournamentShell";

const count = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? "" : "s"}`;

/**
 * When and where, and who plays whom (console spec §5.4). Two views of the
 * same games: By pool is where a pool's games are added and their teams
 * picked; By day is every game in the tournament, pool and bracket, read top
 * to bottom — where a rained-out semifinal is moved and a day's field usage
 * is checked. Results are Scores' job; each row links to its day there.
 */
export function Schedule() {
  const { tid, bundle, caps, conflicts } = useOutletContext<TournamentContext>();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "day" ? "day" : "pool";
  const conflictCount = new Set([...conflicts.values()].flat()).size;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <div role="group" aria-label="Schedule view" className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5 shadow-sm">
          <ViewButton on={view === "pool"} onClick={() => setParams({}, { replace: true })}>
            By pool
          </ViewButton>
          <ViewButton on={view === "day"} onClick={() => setParams({ view: "day" }, { replace: true })}>
            By day
          </ViewButton>
        </div>
        {conflictCount > 0 ? <Badge tone="amber">{count(conflictCount, "conflict")}</Badge> : null}
      </div>
      {view === "pool" ? (
        <PoolScheduleSection tid={tid} bundle={bundle} conflicts={conflicts} caps={caps} />
      ) : (
        <ByDay tid={tid} bundle={bundle} conflicts={conflicts} />
      )}
    </div>
  );
}

function ViewButton({ on, onClick, children }: { on: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cx(
        "rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600",
        on ? "bg-navy-950 text-white" : "text-slate-600 hover:text-navy-950",
      )}
    >
      {children}
    </button>
  );
}

/**
 * Every game by calendar day in the tournament's zone, pool and bracket
 * interleaved by time, Unscheduled last. A bracket game's sides come from the
 * resolver; its row is where a director gives it a time and a field.
 */
function ByDay({
  tid,
  bundle,
  conflicts,
}: {
  tid: string;
  bundle: Bundle;
  conflicts: Map<string, ConflictWarning[]>;
}) {
  const byDay = groupByDay(bundle.games, bundle.tournament.timezone);
  const resolved = new Map<string, ResolvedBracketGame>();
  for (const d of bundle.divisions) {
    if (bundle.games.some((g) => g.divisionId === d.id && g.type === "bracket")) {
      for (const [id, g] of bracketGamesById(bundle, d.id)) resolved.set(id, g);
    }
  }
  if (byDay.size === 0) {
    return (
      <SectionCard title="By day">
        <p className="text-sm text-slate-400">No games yet — add them under By pool.</p>
      </SectionCard>
    );
  }
  return (
    <div className="space-y-6">
      {[...byDay.entries()].map(([day, games]) => (
        <SectionCard key={day} title={dayLabel(day)} badge={<Badge>{games.length}</Badge>}>
          <ul className="space-y-2">
            {games.map((g) => (
              <DayRow key={g.id} tid={tid} bundle={bundle} game={g} resolved={resolved.get(g.id)} warnings={conflicts.get(g.id) ?? []} />
            ))}
          </ul>
        </SectionCard>
      ))}
    </div>
  );
}

function DayRow({
  tid,
  bundle,
  game,
  resolved,
  warnings,
}: {
  tid: string;
  bundle: Bundle;
  game: Game;
  resolved?: ResolvedBracketGame;
  warnings: ConflictWarning[];
}) {
  const division = bundle.divisions.find((d) => d.id === game.divisionId)?.name ?? "";
  const where =
    game.type === "bracket"
      ? resolved
        ? `Gm ${resolved.gameNumber}`
        : "Bracket"
      : (bundle.pools.find((p) => p.id === game.poolId)?.name ?? "");
  const home = resolved ? slotLabel(bundle, resolved.home) : teamName(bundle, game.homeTeamId);
  const away = resolved ? slotLabel(bundle, resolved.away) : teamName(bundle, game.awayTeamId);
  const result = resultText(game);
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-lg bg-white p-2.5 ring-1 ring-slate-200">
      <span className="min-w-0 flex-1 basis-56 truncate text-sm text-slate-700">
        <span className="mr-1.5 text-xs text-slate-400">{[division, where].filter(Boolean).join(" · ")}</span>
        {home} <span className="text-slate-400">vs</span> {away}
      </span>
      <span className="flex items-center gap-1.5">
        {result ? <span className="text-sm tabular-nums text-slate-600">{result}</span> : null}
        <GameStatusBadge status={game.status} />
        <ButtonLink variant="ghost" size="sm" to={scoresHref(tid, bundle, game)}>
          Score
        </ButtonLink>
      </span>
      <GameScheduleLine tid={tid} bundle={bundle} game={game} warnings={warnings} />
    </li>
  );
}

export function PoolScheduleSection({
  tid,
  bundle,
  conflicts,
  caps,
}: {
  tid: string;
  bundle: Bundle;
  conflicts: Map<string, ConflictWarning[]>;
  caps: Caps;
}) {
  // Pools arrive in key order, so ones created across divisions interleave —
  // and nearly every division has a "Pool A". Ordered by division first, using
  // the division list's own order so these cards run in the same sequence as
  // the division-keyed sections below, then by pool order within it. A pool
  // whose division is missing sinks to the end rather than vanishing.
  //
  // Copied before sorting: `bundle.pools` is react-query's cached array, and
  // sorting it in place would reorder every other section reading the bundle.
  const divisionRank = new Map(bundle.divisions.map((d, i) => [d.id, i]));
  const rank = (p: Pool) => divisionRank.get(p.divisionId) ?? Number.MAX_SAFE_INTEGER;
  const pools = [...bundle.pools].sort((a, b) => rank(a) - rank(b) || a.order - b.order);

  return (
    <SectionCard
      title="Pool schedule"
      description={`Add games to a pool, pick each game's two teams, then set when and where it is played. Results are recorded under Scores. Times are ${bundle.tournament.timezone}.`}
    >
      {pools.length === 0 ? (
        <p className="text-sm text-slate-400">
          {/* Pools are the director's to make, so the hint names the page
              they are made on for anyone who runs the event. */}
          {caps.runEvent
            ? "Create pools under Setup to schedule games."
            : "Nothing is scheduled yet — an organizer sets the pools up."}
        </p>
      ) : null}
      {pools.map((p: Pool) => (
        <PoolSchedule key={p.id} tid={tid} bundle={bundle} pool={p} conflicts={conflicts} />
      ))}
    </SectionCard>
  );
}

/**
 * One pool's schedule, built a few games at a time: Add games creates empty
 * games spaced after the pool's last one, and each row's two dropdowns fill
 * its slots. Nothing here pairs teams up
 * for the admin — a pool is not required to be a round-robin, and the orders
 * this replaced could not express the schedules directors actually run.
 *
 * Its own component rather than a block inside `Schedule`'s `map`, because the
 * create error belongs to one pool and hooks cannot be called from a loop body.
 */
function PoolSchedule({
  tid,
  bundle,
  pool,
  conflicts,
}: {
  tid: string;
  bundle: Bundle;
  pool: Pool;
  conflicts: Map<string, ConflictWarning[]>;
}) {
  const [error, setError] = useState<string>();
  const refresh = useAdminMutation(tid, () => Promise.resolve());

  // Games arrive in ULID key order, so a new one lands at the bottom of the
  // list it was added to.
  //
  // Filtered on `type` as well as pool: generation could only ever put a poolId
  // on a pool game, but `createGameSchema` accepts one on a bracket game, and
  // such a record would otherwise land here with team dropdowns whose every
  // save is refused — while also rendering under Bracket games below.
  const poolGames = bundle.games.filter((g) => g.type === "pool" && g.poolId === pool.id);

  // "Pool A" alone names nothing the moment a second division has one too,
  // which is the normal case — the division (the age group) is what tells two
  // of them apart. Dropped entirely when the division is missing, rather than
  // heading the card with a dangling separator.
  const divisionName = bundle.divisions.find((d) => d.id === pool.divisionId)?.name;

  const [howMany, setHowMany] = useState("1");
  const n = Math.min(20, Math.max(1, Math.floor(Number(howMany) || 1)));

  /**
   * Add games ×N (console spec §5.4): N POSTs in sequence, each defaulting
   * its time and place from the game before it — the pool's latest for the
   * first, the previous POST's response after that — so a 5-team pool's ten
   * games are one action and the rows arrive spaced on one field.
   */
  const addGames = async () => {
    const length = bundle.tournament.config.gameLengthMinutes;
    const first = latestGame(poolGames);
    const r = await runSequentially<Game>(
      Array.from({ length: n }, (_, k) => (previous?: Game) =>
        api.post<Game>(`/admin/tournaments/${tid}/games`, {
          divisionId: pool.divisionId,
          type: "pool",
          poolId: pool.id,
          ...nextGameSlot(k === 0 ? first : previous, length),
        }),
      ),
    );
    for (let i = 0; i < r.done; i++) trackGameCreated();
    setError(
      r.error
        ? apiErrorMessage(r.error, `Could not add every game — ${r.done} of ${n} were added.`)
        : undefined,
    );
    refresh.mutate();
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="font-display font-semibold text-navy-950">
          {divisionName ? `${divisionName} · ${pool.name}` : pool.name}
        </span>
        <form
          className="flex items-center gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            void addGames();
          }}
        >
          <Input
            uiSize="sm"
            type="number"
            min={1}
            max={20}
            className="w-16"
            value={howMany}
            onChange={(e) => setHowMany(e.target.value)}
            aria-label={`Games to add to ${divisionName ? `${divisionName} · ${pool.name}` : pool.name}`}
          />
          <Button type="submit" variant="secondary" size="sm">
            Add games
          </Button>
        </form>
      </div>
      {error ? <p className="mb-2 text-xs text-red-600">{error}</p> : null}
      <ul className="space-y-2">
        {poolGames.length === 0 ? (
          <li className="text-xs text-slate-400">
            No games yet — Add games creates them, then pick each game’s two teams.
          </li>
        ) : (
          poolGames.map((g) => (
            <GameRow key={g.id} tid={tid} bundle={bundle} game={g} warnings={conflicts.get(g.id) ?? []} />
          ))
        )}
      </ul>
    </div>
  );
}

/** One assignable side of a pool game. `""` is the TBD option. */
function TeamSlot({
  label,
  value,
  options,
  bundle,
  onChange,
}: {
  label: string;
  value: string | undefined;
  options: TeamOption[];
  bundle: Bundle;
  onChange: (teamId: string) => void;
}) {
  return (
    <Select
      uiSize="sm"
      className="min-w-0 flex-1"
      aria-label={label}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">TBD</option>
      {options.map((o) => (
        <option key={o.teamId} value={o.teamId}>
          {teamLabelById(bundle, o.teamId)}
        </option>
      ))}
    </Select>
  );
}

/** "5–2", home first as the pickers read, for a game with a result. */
export function resultText(game: Game): string | null {
  return game.status === "final" && game.homeScore !== undefined && game.awayScore !== undefined
    ? `${game.homeScore}–${game.awayScore}`
    : null;
}

/** Where this game is scored: its day on the Scores page. */
export function scoresHref(tid: string, bundle: Bundle, game: Game): string {
  return `${phasePath(tid, "scores")}?day=${gameDayKey(game, bundle.tournament.timezone)}`;
}

function GameRow({
  tid,
  bundle,
  game,
  warnings,
}: {
  tid: string;
  bundle: Bundle;
  game: Game;
  warnings: ConflictWarning[];
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string>();
  const save = useAdminMutation(tid, (): Promise<unknown> => Promise.resolve());

  const run = (call: Promise<unknown>, fallback: string) =>
    call
      .then(() => {
        setError(undefined);
        save.mutate();
      })
      .catch((e) => setError(apiErrorMessage(e, fallback)));

  // `null`, not the empty string: it is what clears the slot back to TBD, where
  // an omitted key would leave the team where it was.
  const assign = (side: "homeTeamId" | "awayTeamId", teamId: string) =>
    run(
      api.put(`/admin/tournaments/${tid}/games/${game.id}`, { [side]: teamId || null }),
      "Could not save this team.",
    );

  // Two-step, like the status buttons: this page has no dialog primitive, and
  // one stray click on a row full of dropdowns should not lose a game.
  const remove = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setConfirmingDelete(false);
    void run(api.del(`/admin/tournaments/${tid}/games/${game.id}`), "Could not delete this game.");
  };

  const options = poolTeamOptions(bundle, game);
  const result = resultText(game);

  return (
    <li className="flex flex-wrap items-center gap-2 rounded-lg bg-white p-2.5 ring-1 ring-slate-200">
      <span className="flex min-w-0 flex-1 basis-64 items-center gap-1.5">
        <TeamSlot
          label="Home team"
          value={game.homeTeamId}
          options={options}
          bundle={bundle}
          onChange={(v) => void assign("homeTeamId", v)}
        />
        <span className="shrink-0 text-xs text-slate-400">vs</span>
        <TeamSlot
          label="Away team"
          value={game.awayTeamId}
          options={options}
          bundle={bundle}
          onChange={(v) => void assign("awayTeamId", v)}
        />
      </span>
      <span className="flex items-center gap-1.5">
        {result ? <span className="text-sm tabular-nums text-slate-600">{result}</span> : null}
        <GameStatusBadge status={game.status} />
        {/* The result is entered on Scores — one page for the job, sized for
            a phone — so the row only links to the game's day there. */}
        <ButtonLink variant="ghost" size="sm" to={scoresHref(tid, bundle, game)}>
          Score
        </ButtonLink>
        <Button
          size="sm"
          variant={confirmingDelete ? "danger" : "ghost"}
          onClick={remove}
          onBlur={() => setConfirmingDelete(false)}
        >
          {confirmingDelete ? "Confirm delete" : "Delete"}
        </Button>
      </span>
      {error ? <span className="basis-full text-xs text-red-600">{error}</span> : null}
      <GameScheduleLine tid={tid} bundle={bundle} game={game} warnings={warnings} />
    </li>
  );
}
